// packages/web/src/lib/websocket/manager.ts
// WebSocket connection management

import { getApiBaseUrl } from '../../utils/apiBase';
import { getTenantId } from '../auth/session-manager';

let wsConnection: WebSocket | null = null;
let wsReconnectTimeout: NodeJS.Timeout | null = null;
const wsEventListeners: Record<string, Set<(data: any) => void>> = {};

function getWebSocketUrl(): string {
  const baseUrl = getApiBaseUrl().replace(/^http/, 'ws').replace(/\/api$/, '');
  const tenantId = getTenantId();
  return `${baseUrl}/ws?tenant=${tenantId}`;
}

function connectWebSocket() {
  if (wsConnection?.readyState === WebSocket.OPEN) return;

  try {
    const url = getWebSocketUrl();
    wsConnection = new WebSocket(url);

    wsConnection.onopen = () => {
      console.log('[WS] Connected to order status updates');
      if (wsReconnectTimeout) {
        clearTimeout(wsReconnectTimeout);
        wsReconnectTimeout = null;
      }
    };

    wsConnection.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        const { event: eventType, ...data } = message;

        // Dispatch to registered listeners
        const listeners = wsEventListeners[eventType];
        if (listeners) {
          listeners.forEach(callback => {
            try {
              callback(data);
            } catch (error) {
              console.error('[WS] Error in event listener:', error);
            }
          });
        }
      } catch (error) {
        console.error('[WS] Error parsing message:', error);
      }
    };

    wsConnection.onclose = () => {
      console.log('[WS] Connection closed, attempting reconnect...');
      wsReconnectTimeout = setTimeout(connectWebSocket, 5000);
    };

    wsConnection.onerror = (error) => {
      console.error('[WS] Connection error:', error);
    };
  } catch (error) {
    console.error('[WS] Failed to connect:', error);
  }
}

export function subscribeToOrderStatusUpdates(callback: (data: { order: { id: string; status: string; updatedAt: string } }) => void): () => void {
  const eventType = 'order_status_update';

  if (!wsEventListeners[eventType]) {
    wsEventListeners[eventType] = new Set();
  }

  wsEventListeners[eventType].add(callback);

  // Ensure WebSocket is connected
  if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
    connectWebSocket();
  }

  // Return unsubscribe function
  return () => {
    wsEventListeners[eventType]?.delete(callback);
    if (wsEventListeners[eventType]?.size === 0) {
      delete wsEventListeners[eventType];
    }
  };
}

export function disconnectWebSocket() {
  if (wsConnection) {
    wsConnection.close();
    wsConnection = null;
  }
  if (wsReconnectTimeout) {
    clearTimeout(wsReconnectTimeout);
    wsReconnectTimeout = null;
  }
}